import React, { useCallback, useMemo } from 'react';
import { Form, FormProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import normalize, { JsonApiResponseSingle } from 'json-api-normalizer';
import { first } from 'lodash';
import build from 'redux-object';
import styled from 'styled-components';

import EntityPath from 'constants/entitiesPaths';
import { ProjectionType } from 'constants/projectionTypes';
import { wrapWithStore } from 'helpers/store';
import { container } from 'helpers/styles/mixins';
import { useRwdQuery } from 'helpers/styles/useRwdQuery';
import { CaseType } from 'types/entities/clientCase';
import type { OnboardingInvitation } from 'types/entities/onboardingInvitation';
import { useGetDefaultAvailableCurrency } from 'components/CurrencyInputs/hooks/useGetDefaultAvailableCurrency';
import Content from 'components/Dashboard/Content/Content';
import PageContent from 'components/Dashboard/Content/PageContent';
import OnboardingBackButton from 'components/OnboardingForm/components/shared/OnboardingBackButton';
import OnboardingForm from 'components/OnboardingForm/components/shared/OnboardingForm';
import { Step, useOnboardingSteps } from 'components/OnboardingForm/OnboardingSteps';
import { Fields, Values } from 'components/OnboardingForm/types';
import useActiveStep from 'components/OnboardingForm/useActiveStep';
import AuthApp from 'AuthApp';

import InvitationErrorMessage from './components/InvitationErrorMessage/InvitationErrorMessage';
import OnboardingProgressNav from './components/OnboardingProgressNav/OnboardingProgressNav';

const ProgressNavDesktopWrapper = styled.div`
  ${container()};
`;

interface Props {
  invitation?: JsonApiResponseSingle;
  invitationAlreadyAccepted?: boolean;
  invitationHasExpired?: boolean;
}

function SignUpPage({ invitation, invitationAlreadyAccepted, invitationHasExpired }: Props) {
  const defaultCurrency = useGetDefaultAvailableCurrency();

  const parsedInvitation = useMemo(
    () =>
      invitation
        ? first(build<OnboardingInvitation>(normalize(invitation), EntityPath.Invitations))
        : undefined,
    [invitation]
  );

  const { onboardingSteps, isPreferredAdviserStepEnabled, isDisclosureStepEnabled } =
    useOnboardingSteps({
      wasInvited: Boolean(parsedInvitation),
    });

  const [activeStep, setActiveStep] = useActiveStep(
    invitation ? Step.BeginKnownClient : Step.Begin
  );

  const submit = useCallback<FormProps<Values>['onSubmit']>(
    (values, form) => {
      const { onNext } = onboardingSteps[activeStep];

      if (onNext) {
        return onNext(values, form, setActiveStep);
      }
    },
    [activeStep, setActiveStep, onboardingSteps]
  );

  const { isDesktop } = useRwdQuery();

  const { onPrev, component: StepComponent } = onboardingSteps[activeStep];

  return (
    <AuthApp>
      {invitationAlreadyAccepted || invitationHasExpired ? (
        <>
          <Content>
            <InvitationErrorMessage
              text={
                invitationAlreadyAccepted
                  ? 'onboarding.stepBegin.invitationAlreadyAcceptedDescription'
                  : 'onboarding.stepBegin.invitationHasExpiredDescription'
              }
            />
          </Content>
        </>
      ) : (
        <>
          <Form<Values>
            mutators={{ ...arrayMutators }}
            initialValues={
              {
                [Fields.InvitationUuid]: parsedInvitation ? parsedInvitation.uuid : undefined,
                [Fields.FirstName]: parsedInvitation ? parsedInvitation.firstName : '',
                [Fields.LastName]: parsedInvitation ? parsedInvitation.lastName : '',
                [Fields.Email]: parsedInvitation ? parsedInvitation.email : '',
                [Fields.InvitationMessage]: parsedInvitation
                  ? parsedInvitation.onboardingMessage
                  : undefined,
                [Fields.CaseType]: CaseType.MeetingRequestCase,
                [Fields.Currency]: defaultCurrency?.code,
                [Fields.ProjectionType]: ProjectionType.Investments,
              } as Values // cast is needed because we do not want to pass all the fields values
            }
            onSubmit={submit}
          >
            {formRenderProps => {
              const { handleSubmit, values, form } = formRenderProps;

              const progressNav = (
                <OnboardingProgressNav
                  values={values}
                  activeStep={activeStep}
                  isPreferredAdviserStepEnabled={isPreferredAdviserStepEnabled}
                  isDisclosureStepEnabled={isDisclosureStepEnabled}
                />
              );

              return (
                <Content sidebar={isDesktop ? progressNav : null}>
                  <PageContent
                    topSection={
                      isDesktop ? null : (
                        <ProgressNavDesktopWrapper>{progressNav}</ProgressNavDesktopWrapper>
                      )
                    }
                  >
                    <OnboardingForm onSubmit={handleSubmit}>
                      {onPrev && (
                        <OnboardingBackButton onClick={() => onPrev(values, form, setActiveStep)} />
                      )}

                      <StepComponent {...formRenderProps} />
                    </OnboardingForm>
                  </PageContent>
                </Content>
              );
            }}
          </Form>
        </>
      )}
    </AuthApp>
  );
}

export default wrapWithStore(SignUpPage);
